import { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseAnonKey } from '../../lib/supabaseClient';
import { ApplicationFormData } from '../../types';
import { useToast } from '../../context/ToastContext';
import { countries, isoCountryCodeMap } from '../../lib/countries';
import { validateStep } from './validation';

export const useApplicationForm = () => {
  const { showToast } = useToast();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationsOpen, setApplicationsOpen] = useState(true);
  const [hiringCheckDone, setHiringCheckDone] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [formData, setFormData] = useState<ApplicationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+1',
    phone: '',
    country: 'United States',
    whatsapp: '',
    city: '',
    gender: '',
    tiktokHandle: '',
    instagramHandle: '',
    youtubeHandle: '',
    contentDuration: "I'm brand new (less than 1 month)",
    brandDeals: 'No',
    brandsIfYes: '',
    goesToGym: 'Yes',
    workoutFrequency: '3-4 times per week',
    gymAccessForFilming: 'Yes',
    fitnessJourney: '',
    videosPerWeek: '5-6 videos per week',
    weeklyCall: 'Yes',
    bestCallTimes: '',
    otherCommitments: ''
  });

  // Check hiring status on mount
  useEffect(() => {
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'applications_open')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value !== undefined) setApplicationsOpen(data.value === 'true');
        setHiringCheckDone(true);
      });
  }, []);

  // Auto-detect user's country from IP on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.country_code && isoCountryCodeMap[data.country_code]) {
          const countryInfo = isoCountryCodeMap[data.country_code];
          if (countries.includes(countryInfo.name)) {
            setFormData(prev => ({
              ...prev,
              country: countryInfo.name,
              phoneCountryCode: countryInfo.phoneCode
            }));
          }
        } else if (data.country_name && countries.includes(data.country_name)) {
          setFormData(prev => ({ ...prev, country: data.country_name }));
        }
      } catch (error) {
        console.log('Could not detect location, using defaults');
      }
    };
    detectCountry();
  }, []);

  const handleChange = useCallback((updates: Partial<ApplicationFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const handleNext = useCallback(() => {
    if (!validateStep(step, formData, termsAgreed, showToast)) return;
    // Capture terms timestamp on step 0
    if (step === 0 && !formData.termsAgreedAt) {
      setFormData(prev => ({ ...prev, termsAgreedAt: new Date().toISOString() }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(prev => prev + 1);
  }, [step, formData, termsAgreed, showToast]);

  const handleBack = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep(prev => prev - 1);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(4, formData, termsAgreed, showToast)) return;
    setIsSubmitting(true);

    try {
      const { data: existingStatus, error: checkError } = await supabase
        .rpc('check_application_status', { p_email: formData.email.toLowerCase().trim() });

      if (checkError) {
        console.error('Error checking application status:', checkError);
      }

      if (existingStatus) {
        if (existingStatus === 'pending') {
          showToast('You already have a pending application. Please check your email for the test video link.', 'error');
          setIsSubmitting(false);
          return;
        }
        if (existingStatus === 'approved') {
          showToast('You are already an approved creator! Please log in to your dashboard.', 'error');
          setIsSubmitting(false);
          return;
        }
      }

      const generateTestToken = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const randomValues = new Uint32Array(32);
        crypto.getRandomValues(randomValues);
        let token = '';
        for (let i = 0; i < 32; i++) {
          token += chars.charAt(randomValues[i] % chars.length);
        }
        return token;
      };

      const testToken = generateTestToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { error: dbError } = await supabase
        .from('applications')
        .insert([{
          name: `${formData.firstName} ${formData.lastName}`,
          social_link: formData.tiktokHandle,
          motivation: formData.fitnessJourney,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email.toLowerCase().trim(),
          phone_country_code: formData.phoneCountryCode,
          phone: formData.phone,
          country: formData.country,
          whatsapp: formData.whatsapp || null,
          city: formData.city,
          gender: formData.gender,
          tiktok_handle: formData.tiktokHandle,
          instagram_handle: formData.instagramHandle,
          youtube_handle: formData.youtubeHandle,
          content_duration: formData.contentDuration,
          brand_deals: formData.brandDeals,
          brands_if_yes: formData.brandsIfYes,
          goes_to_gym: formData.goesToGym,
          workout_frequency: formData.workoutFrequency,
          gym_access_for_filming: formData.gymAccessForFilming,
          fitness_journey: formData.fitnessJourney,
          videos_per_week: formData.videosPerWeek,
          weekly_call: formData.weeklyCall,
          best_call_times: formData.bestCallTimes,
          other_commitments: formData.otherCommitments,
          test_sent_at: new Date().toISOString(),
          status: 'pending',
          terms_agreed_at: formData.termsAgreedAt,
          test_token: testToken,
          test_link_expires_at: expiresAt.toISOString()
        }]);

      if (dbError) throw dbError;

      const { error: emailError } = await supabase.functions.invoke('send-test-instructions', {
        body: { email: formData.email, name: formData.firstName, testToken },
        headers: {
          Authorization: `Bearer ${supabaseAnonKey}`,
          apikey: supabaseAnonKey || ''
        }
      });

      if (emailError) {
        console.error('Email trigger failed:', emailError);
        showToast('Application received, but there was an issue sending the confirmation email. Please contact support if you don\'t receive it.', 'error');
        setTimeout(() => { window.location.href = '/apply/submitted'; }, 3000);
        return;
      }

      window.location.href = '/apply/submitted';
    } catch (error: any) {
      console.error('Error submitting application:', error);
      if (error?.code === '23505' || error?.message?.includes('duplicate') || error?.message?.includes('unique')) {
        showToast('An application with this email already exists. Please check your email for further instructions.', 'error');
      } else {
        showToast('Submission failed. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, termsAgreed, showToast]);

  return {
    step, formData, isSubmitting, applicationsOpen, hiringCheckDone,
    termsAgreed, setTermsAgreed,
    handleChange, handleNext, handleBack, handleSubmit
  };
};
